import { useClickOutside } from '@react-hookz/web';
import { ChevronDown24 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import { company } from '~/company/Company';
import { LOCALE } from '~/components/LanguageSelector/constants';
import { LanguageList } from '~/components/LanguageSelector/LanguageList';
import { useLang } from '~/hooks/useLang';
import { userStore } from '~/stores';

const FooterLanguagePicker = ({ className }: { className?: string }) => {
  const { setLanguage } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useClickOutside(rootRef, () => setIsOpen(false));

  const langs = company.config.langs;
  if (!langs || langs.length < 2) return null;

  const currentLang = userStore.lang || company.config.lang;
  const currentTitle =
    LOCALE[currentLang as keyof typeof LOCALE]?.title ?? currentLang.toUpperCase();

  const handleSelect = (lang: string) => {
    if (lang === currentLang) {
      setIsOpen(false);
      return;
    }
    setLanguage(lang);
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        data-button="footer-change-language"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          'inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-full',
          'border border-navigation-stroke-secondary border-solid bg-transparent px-4',
          'text-navigation-text-primary [font:var(--text-200-semibold)]',
          'transition-colors duration-200 hover:bg-navigation-surface',
        )}
      >
        <span>{currentTitle}</span>
        <ChevronDown24
          width={16}
          height={16}
          className={cn(
            'transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className={cn(
            'absolute right-0 bottom-[calc(100%+8px)] z-10 min-w-[200px]',
            'rounded-4 bg-bg-content p-4 shadow-lg',
          )}
        >
          <LanguageList currentLang={currentLang} onSelect={handleSelect} />
        </div>
      )}
    </div>
  );
};

export default observer(FooterLanguagePicker);
